import cx from 'classnames'
import React, { useCallback, useRef } from 'react'
import { useDropzone } from 'react-dropzone'

import { TextArea } from '../input'

type Props = {
  /** Label to show on the input. */
  label?: string
  /** HTML for attribute */
  htmlFor: string
  /** Current value */
  value: string | number | readonly string[] | undefined
  /** On change function. */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** On blur function. */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
  /** On focus function. */
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
  /** Externally defined className for the input. */
  className?: string
  /** Form field description. */
  description?: string
  /** Placeholder */
  placeholder?: string
  /** True if the area is in invalid state. */
  isInvalid?: boolean
  /** True if the text area has round corners */
  isRounded?: boolean
  /** True if a value for the field is required */
  isRequired?: boolean
  /** Max size of the droppable file in bytes. */
  maxSize?: number
}

/** Droppable TextArea to be used within the scope of a form. */
const DroppableTextArea: React.FC<
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & Props
> = ({
  htmlFor,
  label,
  className,
  description,
  isInvalid,
  isRounded,
  isRequired,
  maxSize,
  onChange,
  ...other
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles[0]

    if (droppedFile) {
      const reader = new FileReader()
      reader.addEventListener('load', (event) => {
        const textValue = (event.target?.result as string | null) || ''
        if (textAreaRef.current) {
          textAreaRef.current.value = textValue
          /** We can't do textAreaRef.current.dispatchEvent here due to
           * https://github.com/facebook/react/issues/11488
           */
          if (onChange) {
            const simulatedChangeEvent = {
              target: { value: textValue, name: htmlFor },
            } as unknown as React.ChangeEvent<HTMLTextAreaElement>
            onChange(simulatedChangeEvent)
          }
        }
      })
      reader.readAsText(droppedFile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { getRootProps, isDragActive } = useDropzone({
    maxSize,
    onDrop,
    maxFiles: 1,
  })

  return (
    <div
      {...getRootProps()}
      className="relative"
      data-testid="dragOverTextArea"
    >
      {isDragActive && (
        <>
          <div
            className={cx(
              'absolute flex h-full w-full items-center justify-center border border-dashed border-primary-500 bg-primary-200 opacity-50',
              { 'rounded-md': isRounded },
            )}
          />
          <div className="absolute flex h-full w-full items-center justify-center">
            Drop your file here!
          </div>
        </>
      )}
      <TextArea
        id={htmlFor}
        className={className}
        isInvalid={isInvalid}
        ref={textAreaRef}
        label={label}
        description={description}
        onChange={onChange}
        required={isRequired}
        {...other}
      />
    </div>
  )
}

DroppableTextArea.defaultProps = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
  isRounded: true,
  isRequired: false,
  isInvalid: false,
  maxSize: 500000,
}

export default DroppableTextArea
